import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, HeartHandshake } from "lucide-react";
import { toast } from "sonner";
import { StatusBadge } from "../../components/shared/StatusBadge";
import { EmptyState } from "../../components/shared/EmptyState";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { api, formatApiError } from "../../lib/api";

export default function CoordinationAdminPage() {
  const [dossiers, setDossiers] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ customer_id: "", formule: 1, create_reservation: true });

  const load = () => {
    api.get("/admin/coordination").then((r) => setDossiers(r.data)).catch(() => setDossiers([]));
    api.get("/admin/customers").then((r) => setCustomers(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    try {
      await api.post("/admin/coordination", { customer_id: form.customer_id, formule: Number(form.formule), create_reservation: form.create_reservation });
      toast.success("Dossier Coordination créé — réservation en attente de finalisation client (CGV + acompte)");
      setOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!dossiers) return <div className="py-16 text-center font-heading text-deepgreen" data-testid="coordination-loading">Chargement…</div>;

  return (
    <div data-testid="admin-coordination-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="overline-ae">Coordination de mariage</p>
          <h1 className="mt-1 font-heading text-2xl sm:text-3xl text-deepgreen">Dossiers Coordination</h1>
          <p className="mt-1 text-sm text-muted-foreground">Prestation facultative et indépendante de la location — jamais imposée, jamais ajoutée automatiquement.</p>
        </div>
        <button type="button" onClick={() => setOpen(true)} className="btn-primary-ae !px-5 !py-2.5 !text-xs" data-testid="coordination-create">
          <Plus className="h-4 w-4" /> Nouveau dossier
        </button>
      </div>

      {dossiers.length === 0 ? (
        <div className="mt-6"><EmptyState icon={HeartHandshake} title="Aucun dossier Coordination">Les dossiers sont créés après échange avec le client (rendez-vous préalable obligatoire).</EmptyState></div>
      ) : (
        <div className="mt-6 grid gap-4 md:grid-cols-2" data-testid="coordination-list">
          {dossiers.map((d) => (
            <Link key={d.id} to={`/admin/coordination/${d.id}`} className="card-ae block p-5 transition-transform duration-200 hover:-translate-y-0.5" data-testid={`coordination-card-${d.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="font-heading text-lg text-deepgreen">{d.formule_name} — {d.formule_price.toLocaleString("fr-FR")} €</p>
                  <p className="mt-1 text-sm text-muted-foreground">{d.customer_name || d.customer_email} — {d.event_title || "Mariage"}{d.event_date ? ` — ${d.event_date}` : ""}</p>
                </div>
                <StatusBadge status={d.status === "en_cours" ? "En attente" : "Passée"} />
              </div>
              <div className="mt-3 flex items-center gap-3 text-xs text-muted-foreground">
                <span className="rounded-full bg-sage-light px-3 py-1 font-semibold text-deepgreen">{d.day_j ? "Présence jour J jusqu'au dessert" : "Sans présence jour J"}</span>
                <span>{d.tasks_done}/{d.tasks_total} tâches</span>
                {d.reservation_id && <span className="text-sage">Réservation liée</span>}
              </div>
            </Link>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-cream" data-testid="coordination-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-deepgreen">Nouveau dossier Coordination</DialogTitle>
            <DialogDescription>Après rendez-vous préalable avec le client. La réservation suit le tunnel contractuel habituel (CGV + acompte 30 %).</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="label-ae" htmlFor="coord-customer">Client</label>
              <select id="coord-customer" className="input-ae" value={form.customer_id} onChange={(e) => setForm({ ...form, customer_id: e.target.value })} data-testid="coordination-customer">
                <option value="">Choisir…</option>
                {customers.map((c) => <option key={c.id} value={c.id}>{c.first_name} {c.last_name} — {c.email}</option>)}
              </select>
            </div>
            <div>
              <label className="label-ae">Formule</label>
              <div className="grid gap-2 sm:grid-cols-3" data-testid="coordination-formules">
                {[1, 2, 3].map((f) => (
                  <button key={f} type="button" onClick={() => setForm({ ...form, formule: f })} data-testid={`coordination-formule-${f}`}
                    className={`rounded-lg border p-3 text-left transition-colors ${form.formule === f ? "border-deepgreen bg-sage-light" : "border-border bg-white hover:border-sage"}`}>
                    <p className="text-sm font-bold text-deepgreen">Formule {f}</p>
                    <p className="text-xs text-muted-foreground">{[790, 1300, 1900][f - 1].toLocaleString("fr-FR")} €</p>
                    <p className="text-[10px] text-muted-foreground">{f === 3 ? "Jusqu'au dessert" : "Sans jour J"}</p>
                  </button>
                ))}
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm font-semibold text-deepgreen">
              <input type="checkbox" checked={form.create_reservation} onChange={(e) => setForm({ ...form, create_reservation: e.target.checked })} className="h-4 w-4 accent-[#2C4C3B]" data-testid="coordination-create-reservation" />
              Créer la réservation Coordination (finalisation client : CGV + acompte)
            </label>
            <button type="button" onClick={submit} disabled={!form.customer_id} className="btn-primary-ae modal-sticky-btn w-full disabled:opacity-50" data-testid="coordination-submit">Créer le dossier</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
